import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";

/**
 * Data layer for listening on the phone.
 *
 * Positions are SERVER-side, not localStorage: the point of the feature is that
 * a listen started on a walk can be picked up at the desk, which a per-device
 * store cannot do.
 */

export interface ListeningPosition {
  project_id: string;
  audio_filename: string;
  position_s: number;
  updated_at: string;
}

export function useListeningPositions() {
  return useQuery({
    queryKey: ["me", "listening"],
    queryFn: () => api<{ positions: ListeningPosition[] }>("/api/me/listening").then((r) => r.positions),
    staleTime: 10_000,
  });
}

/** Save where you are. Throttled by the caller — a save per second of playback
 *  would be a save per second of playback. */
export function useSavePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, audio_filename, position_s }:
      { projectId: string; audio_filename: string; position_s: number }) =>
      api(`/api/projects/${projectId}/listening`, {
        method: "PUT", body: JSON.stringify({ audio_filename, position_s }),
      }),
    // Deliberately quiet: no invalidate on every save. The cache is refreshed
    // when the library is next opened; re-fetching the whole position list every
    // few seconds of playback would be pure noise on a mobile connection.
    onSuccess: () => { qc.setQueryDefaults(["me", "listening"], { staleTime: 10_000 }); },
  });
}

/** Seconds a flag is stamped BEFORE the tap. Per account; the server supplies
 *  the default so there is one source of truth for it rather than two. */
export function useFlagLead() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["me", "flag-lead"],
    queryFn: () => api<{ lead_s: number }>("/api/me/flag-lead").then((r) => r.lead_s),
    staleTime: 300_000,
  });
  const save = useMutation({
    mutationFn: (lead_s: number) =>
      api<{ lead_s: number }>("/api/me/flag-lead", { method: "PUT", body: JSON.stringify({ lead_s }) }),
    onMutate: (lead_s: number) => { qc.setQueryData(["me", "flag-lead"], lead_s); },
    onSettled: () => qc.invalidateQueries({ queryKey: ["me", "flag-lead"] }),
  });
  return { lead: q.data ?? 2, setLead: (n: number) => save.mutate(n) };
}
